import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useTranslation } from "react-i18next";
import "./styles/CreateTeamPage.css";

import SelectCyclists from "./createStep/SelectCyclists.js";
import SelectCyclistsBonus from "./createStep/SelectCyclistsBonus.js";
import { VUELTA_TEAM_ID } from "../config/competitionConfig";

const GIRO_TEAM_ID = 3;
const GIRO_RACE_ID = 87;
const TDF_TEAM_ID = 4;
const TDF_RACE_ID = 122;
const CYCLIST_CATALOG_TEAM_ID = 1;
const SINGLE_RACE_BUDGET = 300;
const VUELTA_BUDGET = 300;

const CreateGiroTeamPage = ({
  teamId: requestedTeamId = GIRO_TEAM_ID,
  raceId: requestedRaceId = GIRO_RACE_ID,
  teamName = "Giro",
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const teamId = requestedTeamId;
  const isVueltaTeam = teamId === VUELTA_TEAM_ID;
  const teamBudget = isVueltaTeam ? VUELTA_BUDGET : SINGLE_RACE_BUDGET;

  const [step, setStep] = useState(1);
  const [budget, setBudget] = useState(teamBudget);
  const [numberPlayerSelected, setNumberPlayerSelected] = useState(0);

  const [selectedCyclists, setSelectedCyclists] = useState([]);
  const [cyclistsBonus, setCyclistsBonus] = useState([]);
  const [cyclists, setCyclists] = useState([]);

  const raceId = isVueltaTeam ? null : requestedRaceId;
  const userCyclistsKey =
    isVueltaTeam
      ? "vueltaCyclists"
      : teamId === TDF_TEAM_ID
      ? "tdfCyclists"
      : teamId === GIRO_TEAM_ID
        ? "giroCyclists"
        : null;

  const nextStep = () => {
    if (step < 2) setStep((s) => s + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep((s) => s - 1);
  };

  const saveData = async () => {
    if (!user) {
      navigate("/logIn");
      throw new Error(t("createTeam.errors.notConnected"));
    }

    const playerTeamData = {
      selectedCyclists,
      cyclistsBonus,
      selectedRaces: isVueltaTeam
        ? []
        : [
            {
              id: raceId,
              raceId,
            },
          ],
      bonusRaces: [],
      teamId,
      leagueId: teamId,
    };

    try {
      const response = await fetch(`${apiUrl}/savePlayerTeam`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(playerTeamData),
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        const backendMessage =
          typeof data?.message === "string" && data.message.trim()
            ? data.message
            : `Erreur HTTP: ${response.status}`;
        throw new Error(backendMessage);
      }

      navigate("/profil");
      return data;
    } catch (error) {
      console.error(`Erreur sauvegarde equipe ${teamName}:`, error);
      throw error;
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/logIn");
      return;
    }

    const fetchExistingSingleRaceTeam = async () => {
      try {
        if (userCyclistsKey) {
          const cyclistsResponse = await fetch(`${apiUrl}/cyclistsListUser`, {
            credentials: "include",
          });

          if (cyclistsResponse.ok) {
            const cyclistsData = await cyclistsResponse.json();
            const existingCyclists = cyclistsData?.[userCyclistsKey] || [];

            if (existingCyclists.length > 0) {
              setSelectedCyclists(existingCyclists);
              setCyclistsBonus(
                existingCyclists.filter(
                  (c) => c.isBonus === true || c.isBonus === 1 || c.isBonus === "1"
                )
              );
              setNumberPlayerSelected(existingCyclists.length);
              return;
            }
          }
        }

        const response = await fetch(`${apiUrl}/userTeam/${teamId}`, {
          credentials: "include",
        });

        if (!response.ok) return;

        const data = await response.json();

        if (data) {
          setSelectedCyclists(data.selectedCyclists || []);
          setCyclistsBonus(data.cyclistsBonus || []);
          setNumberPlayerSelected((data.selectedCyclists || []).length);
        }
      } catch (error) {
        console.error(`Erreur recuperation equipe ${teamName}:`, error);
      }
    };

    fetchExistingSingleRaceTeam();
  }, [apiUrl, user, navigate, teamId, teamName, userCyclistsKey]);

  useEffect(() => {
    if (!user) {
      navigate("/logIn");
      return;
    }

    const fetchCyclists = async () => {
      try {
        const response = await fetch(
          `${apiUrl}/cyclists-list/${CYCLIST_CATALOG_TEAM_ID}`
        );

        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }

        const data = await response.json();

        const filtered = (data || []).filter((c) => {
          const groupId = Number(c.cyclistGroupId ?? c.cyclist_group_id ?? 1);
          return groupId === 1;
        });

        setCyclists(filtered);
      } catch (error) {
        console.error(`Erreur recuperation coureurs ${teamName}:`, error);
      }
    };

    fetchCyclists();
  }, [apiUrl, user, navigate, teamName]);

  useEffect(() => {
    const calculateInitialBudget = (totalBudget) => {
      const totalValueOfSelectedCyclists = selectedCyclists.reduce(
        (total, selectedCyclist) => {
          const cyclist = cyclists.find(
            (c) => c.cyclistId === selectedCyclist.cyclistId
          );

          return total + (cyclist ? Number(cyclist.finalValue || 0) : 0);
        },
        0
      );

      return totalBudget - totalValueOfSelectedCyclists;
    };

    setBudget(calculateInitialBudget(teamBudget));
  }, [selectedCyclists, cyclists, teamBudget]);

  switch (step) {
    case 1:
      return (
        <SelectCyclists
          numberPlayerSelected={numberPlayerSelected}
          setNumberPlayerSelected={setNumberPlayerSelected}
          budget={budget}
          setBudget={setBudget}
          selectedCyclists={selectedCyclists}
          setSelectedCyclists={setSelectedCyclists}
          setCyclistsBonus={setCyclistsBonus}
          cyclistsBonus={cyclistsBonus}
          cyclists={cyclists}
          teamId={teamId}
          nextStep={nextStep}
        />
      );

    case 2:
      return (
        <SelectCyclistsBonus
          selectedCyclists={selectedCyclists}
          setCyclistsBonus={setCyclistsBonus}
          cyclistsBonus={cyclistsBonus}
          nextStep={saveData}
          prevStep={prevStep}
          teamId={teamId}
          saveData={saveData}
        />
      );

    default:
      return <div>{t("createTeam.errors.unknownStep")}</div>;
  }
};

export { GIRO_TEAM_ID, GIRO_RACE_ID, TDF_TEAM_ID, TDF_RACE_ID };
export default CreateGiroTeamPage;
